/**
 * Discovery / Inventory - cross-source reconciliation (release 3.6 item 30).
 *
 * One row per kind of personal data, one column per source type, or per
 * database, bucket, share or device (T-30): which sources hold it, how many
 * assets and rows, and where presence or counts disagree. Downloadable as CSV.
 *   • GET /v1/discovery/assets/reconciliation[?by=store][&format=csv]
 *
 * Loads on its own so a failure here never hides the rest of the inventory, and
 * a failed load says so rather than reading as "nothing disagrees".
 *
 * Runs only when the person asks (review of #187, round 4): the report reads
 * every live finding (about 10 s at 100,000 assets and 1,000,000 findings), so
 * it is not run on every Inventory load. The server keeps one answer per tenant
 * and business-unit scope for 5 minutes, stops a run at its time ceiling and
 * refuses a new one while it is busy. Both answer a 503 problem
 * (`application/problem+json`) whose `detail` this card shows as the server's
 * own plain reason: apiFetch parses any JSON media type, `+json` included
 * (round 6; before it, both read "503 Service Unavailable"). Proved through
 * the real apiFetch in __tests__/cross-source-reconciliation-api-fetch.test.tsx.
 *
 * T-30 history: after a report loads, the stored snapshots for the same
 * granularity and the reader's own scope, as a trend of presence gaps and the
 * kinds newly not found since the previous snapshot (drift). The CSV download
 * is recorded in the audit trail by the server.
 *   • GET /v1/discovery/assets/reconciliation/history?by=
 * A failed history load says so; it never hides the report.
 */

import { useCallback, useRef, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassCard, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  downloadCrossSourceReconciliationCsv,
  getCrossSourceReconciliation,
  getReconciliationHistory,
  problemDetail,
  type CrossSourceReconciliation,
  type ReconciliationGranularity,
  type ReconciliationSnapshot,
} from '@/lib/api';

// There is no "Absent": nothing recorded proves a source's scanned set is
// complete (a table the scanner could not read leaves no asset), so the
// strongest negative the data supports is "not found in scanned assets".
const CELL_TEXT = {
  present: 'Present',
  name_only: 'Labelled, no live value match (a guess)',
  not_found: 'Not found in scanned assets',
  partial: 'Partly scanned, not found so far',
  not_scanned: 'Not scanned',
} as const;

const shortDate = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

/** A small line of presence gaps over the snapshots, oldest on the left. */
function GapSparkline({ points }: { points: ReconciliationSnapshot[] }) {
  if (points.length < 2) return null;
  const w = 160;
  const h = 32;
  const max = Math.max(1, ...points.map((p) => p.presenceGaps));
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${(i * step).toFixed(1)},${(h - (p.presenceGaps / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={`Not found in a scanned source over ${points.length} snapshots, from ${points[0]!.presenceGaps} to ${points[points.length - 1]!.presenceGaps}`}
      data-testid="reconciliation-trend-line"
    >
      <polyline points={d} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-sev-medium" />
    </svg>
  );
}

export function CrossSourceReconciliationCard() {
  const [report, setReport] = useState<CrossSourceReconciliation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [by, setBy] = useState<ReconciliationGranularity>('source_type');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [history, setHistory] = useState<ReconciliationSnapshot[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const run = useRef(0);

  // T-30: a report answers one granularity; switching asks again, never relabels the old one.
  const switchBy = useCallback((next: ReconciliationGranularity) => {
    run.current += 1;
    setBy(next);
    setReport(null);
    setError(null);
    setExportError(null);
    setHistory(null);
    setHistoryError(null);
    setLoading(false);
  }, []);

  const exportCsv = useCallback(() => {
    setExporting(true);
    setExportError(null);
    downloadCrossSourceReconciliationCsv(by)
      .catch((err: unknown) => setExportError(problemDetail(err, 'Export failed')))
      .finally(() => setExporting(false));
  }, [by]);

  const load = useCallback(() => {
    const mine = ++run.current;
    setLoading(true);
    setError(null);
    setHistory(null);
    setHistoryError(null);
    getCrossSourceReconciliation(by)
      .then((r) => {
        if (run.current !== mine) return;
        setReport(r);
        // The history is read after the report, so the snapshot this run may
        // have just written is in it.
        getReconciliationHistory(by)
          .then((h) => { if (run.current === mine) setHistory(h.snapshots); })
          .catch((err: unknown) => {
            if (run.current === mine) setHistoryError(problemDetail(err, 'Failed to load the history'));
          });
      })
      .catch((err: unknown) => {
        if (run.current === mine) {
          setReport(null);
          setError(problemDetail(err, 'Failed to load reconciliation'));
        }
      })
      .finally(() => { if (run.current === mine) setLoading(false); });
  }, [by]);

  return (
    <GlassCard className="p-6 mt-6" data-testid="cross-source-reconciliation">
      <CardHeader className="p-0 mb-3">
        <CardTitle>Cross-source reconciliation</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          The same kind of personal data across your sources. Present means a live finding (open, in
          progress or an accepted risk) matched the values themselves in at least one asset. Labelled, no
          live value match means an asset carries the label but no live finding matched its values: a
          column named for it, or a value finding that has since been closed (resolved or a false
          positive). A guess, not evidence. Scanned here means some of an asset&apos;s values were read: a
          finding matched its values, or the scanner reported reading one of its columns after a complete
          run. An asset only crawled, posture-checked or uploaded does not count. A table counts as
          scanned once any of its columns was read for values, so a column that was not read in such a
          table can still show Not found in scanned assets: a lead, not proof. Not found in scanned
          assets means each asset Discovery knows of in the source was scanned and none showed it. That is not proof the source holds none: a table the
          scanner could not read, or was never pointed at, is not in the inventory. Partly scanned, not
          found so far means only some of the source&apos;s assets have been scanned. Not scanned means
          none of its assets has been scanned, as far as Discovery was told, so it can include assets
          scanned with no match. Differing row counts are shown, not judged: a transactions table holds
          more rows than a master.
        </p>
      </CardHeader>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" role="group" aria-label="Compare by">
        <span className="text-muted-foreground">Compare by</span>
        <Button
          variant={by === 'source_type' ? 'default' : 'glass'}
          size="sm"
          aria-pressed={by === 'source_type'}
          onClick={() => switchBy('source_type')}
          data-testid="reconciliation-by-source-type"
        >
          Source type
        </Button>
        <Button
          variant={by === 'store' ? 'default' : 'glass'}
          size="sm"
          aria-pressed={by === 'store'}
          onClick={() => switchBy('store')}
          data-testid="reconciliation-by-store"
        >
          Database or store
        </Button>
        {by === 'store' && (
          <span className="text-muted-foreground">
            Each database, bucket, share or device is its own column, so two databases of one engine can disagree.
          </span>
        )}
      </div>

      {report?.businessUnitScoped && (
        <p role="note" className="text-xs text-sev-medium mb-3" data-testid="reconciliation-bu-scope">
          This view is limited to the business units you can see (and assets with no unit). Sources
          and assets in other units are not counted, so Not found here says nothing about them.
        </p>
      )}

      {!report && !loading && (
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Button variant="glass" size="sm" onClick={load} data-testid="reconciliation-run">
            {error ? 'Try again' : 'Run the reconciliation'}
          </Button>
          <span className="text-xs text-muted-foreground">
            It reads every live finding, so it runs when you ask. One answer is kept for 5 minutes.
          </span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading reconciliation…
        </div>
      )}

      {error && !loading && (
        <div role="alert" className="flex items-center gap-2 text-xs text-sev-high">
          <AlertTriangle className="h-3.5 w-3.5" /> Could not load the reconciliation: {error}
        </div>
      )}

      {report && (
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Button variant="glass" size="sm" onClick={exportCsv} disabled={exporting} data-testid="reconciliation-export-csv">
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
          <span className="text-xs text-muted-foreground">The download is recorded in the audit trail.</span>
          {exportError && (
            <span role="alert" className="text-xs text-sev-high">Could not export: {exportError}</span>
          )}
        </div>
      )}

      {report && (
        <p className="text-xs text-muted-foreground mb-2" data-testid="reconciliation-as-of">
          As of {new Date(report.generatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}.
          Findings that arrived since then show after the next run (answers are kept for 5 minutes).
        </p>
      )}

      {report && report.labels.length === 0 && (
        <p className="text-xs text-muted-foreground">No classified assets yet, so there is nothing to reconcile.</p>
      )}

      {report && report.labels.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="glass">{report.summary.heldInSeveralSources} held in several sources</Badge>
            <Badge variant={report.summary.presenceGaps > 0 ? 'warning' : 'glass'}>
              {report.summary.presenceGaps} not found in a scanned source
            </Badge>
            <Badge variant={report.summary.countDisagreements > 0 ? 'warning' : 'glass'}>
              {report.summary.countDisagreements} count disagreements
            </Badge>
          </div>
          {report.sourcesTruncated && (
            <p role="status" className="text-xs text-sev-high mb-2" data-testid="reconciliation-sources-truncated">
              The report is cut short: more stores exist than it can compare (the 200 with the most assets
              are shown), so a store left out can hold any of these.
            </p>
          )}
          {report.truncated && (
            <p role="status" className="text-xs text-sev-high mb-2">
              The report is cut short: more labels exist than it can list (the first 5,000 by name are
              shown).
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-4">Personal data</th>
                  {report.sources.map((s) => (
                    <th key={s.sourceType} className="py-2 pr-4 font-mono normal-case">
                      {s.sourceType}
                      {s.kind && s.kind !== s.sourceType && <span className="block text-muted-foreground">{s.kind}</span>}
                    </th>
                  ))}
                  <th className="py-2">Disagreement</th>
                </tr>
              </thead>
              <tbody>
                {report.labels.map((l) => (
                  <tr key={l.label} className="border-t border-border/30" data-testid={`reconciliation-row-${l.label}`}>
                    <td className="py-2 pr-4 font-mono">{l.label}</td>
                    {l.cells.map((c) => (
                      <td key={c.sourceType} className="py-2 pr-4 tabular">
                        {CELL_TEXT[c.status]}
                        {c.status === 'name_only' && (
                          <span className="text-muted-foreground">
                            {' '}({c.nameOnlyAssetCount.toLocaleString('en-IN')} assets)
                          </span>
                        )}
                        {c.status === 'present' && (
                          <span className="text-muted-foreground">
                            {' '}({c.assetCount.toLocaleString('en-IN')} assets, {c.rowCount === null ? 'rows unknown' : `${c.rowCount.toLocaleString('en-IN')} rows`})
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="py-2">
                      {[l.presenceGap && 'Not found in a scanned source', l.countsDisagree && 'Counts'].filter(Boolean).join(' and ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {report && (
        <div className="mt-4" data-testid="reconciliation-history">
          <p className="text-xs font-medium mb-1">History</p>
          {historyError && (
            <p role="alert" className="text-xs text-sev-high">Could not load the history: {historyError}</p>
          )}
          {!historyError && history === null && <p className="text-xs text-muted-foreground">Loading history…</p>}
          {history !== null && history.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No snapshots yet. One is kept each time the report is worked out, and once a day for the whole estate.
            </p>
          )}
          {history !== null && history.length > 0 && (
            <>
              <div className="flex items-center gap-3 mb-1">
                <GapSparkline points={history} />
                <span className="text-xs text-muted-foreground">
                  Not found in a scanned source, {history.length} snapshot{history.length === 1 ? '' : 's'} since {shortDate(history[0]!.takenAt)}.
                </span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-1 pr-4">Taken</th>
                    <th className="py-1 pr-4">Not found in a scanned source</th>
                    <th className="py-1 pr-4">Count disagreements</th>
                    <th className="py-1">New since the previous one</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().slice(0, 10).map((h) => (
                    <tr key={h.takenAt} className="border-t border-border/30" data-testid="reconciliation-history-row">
                      <td className="py-1 pr-4">{shortDate(h.takenAt)}{h.trigger === 'scheduled' ? ' (daily)' : ''}</td>
                      <td className="py-1 pr-4 tabular">{h.presenceGaps.toLocaleString('en-IN')}</td>
                      <td className="py-1 pr-4 tabular">{h.countDisagreements.toLocaleString('en-IN')}</td>
                      <td className="py-1">
                        {h.newGapLabels.length === 0 ? '-' : (
                          <span className={h.drifted ? 'text-sev-high' : undefined}>
                            {h.newGapLabels.join(', ')}{h.drifted ? ' (drift)' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </GlassCard>
  );
}
